import { ReactNode } from 'react';
import {
  Box,
  Card as ChakraCardNamespaceBase,
  CardRootProps as ChakraCardRootProps,
  CardTitleProps as ChakraCardTitleProps,
  Heading,
} from '@chakra-ui/react';

import { LEGACY_MODE, useMode } from '../Mode';

const CONTENT_PADDING = 6;

/**
 * Stretches a child within a `CardContent` or `SimpleCard` to not have side padding
 * (e.g. for a table alongside text content)
 */
export function CardFullWidthContainer({
  children,
  isFirstChild = false,
  isLastChild = false,
}: {
  children: ReactNode;
  /**
   * If the full width content is the first child, remove the top padding
   *
   * Not using CSS as this element may not always be the "true" first DOM child if there's nesting
   */
  isFirstChild?: boolean;

  /**
   * If the full width content is the last child, remove the bottom padding
   *
   * Not using CSS as this element may not always be the "true" last DOM child if there's nesting
   */
  isLastChild?: boolean;
}) {
  return (
    // Extra Box allows this to work in a `VStack`
    <Box>
      <Box
        marginX={-CONTENT_PADDING}
        marginTop={isFirstChild ? -CONTENT_PADDING : undefined}
        marginBottom={isLastChild ? -CONTENT_PADDING : undefined}
      >
        {children}
      </Box>
    </Box>
  );
}

export interface CardRootProps extends ChakraCardRootProps {
  ref?: React.RefObject<HTMLDivElement | null>;
}

export const CardRoot = function CardRoot({ ref, ...props }: CardRootProps) {
  return (
    <ChakraCardNamespace.Root ref={ref} {...props} as="section">
      {props.children}
    </ChakraCardNamespace.Root>
  );
};

export const CardBody = ChakraCardNamespaceBase.Body;
export const CardHeader = ChakraCardNamespaceBase.Header;
export const CardFooter = ChakraCardNamespaceBase.Footer;

export interface CardTitleProps extends ChakraCardTitleProps {
  ref?: React.RefObject<HTMLDivElement | null>;
}

export function CardTitle({ ref, ...props }: CardTitleProps) {
  const { children, ...rest } = props;
  const { mode } = useMode();

  return (
    <ChakraCardNamespace.Title asChild ref={ref} {...rest}>
      <Heading as="h2" size={mode === 'legacy' ? 'md' : 'lg'}>
        {children}
      </Heading>
    </ChakraCardNamespace.Title>
  );
}

// eslint-disable-next-line @typescript-eslint/naming-convention -- Component names PascalCase
export const ChakraCardNamespace = { ...ChakraCardNamespaceBase, FullWidthRoot: CardFullWidthContainer };

/**
 * Simplified Card API for common cases, as the composable card elements get a bit repetitive.
 *
 * Elements remain composable for more elaborate cases, so avoid adding more complexity here
 */
export function SimpleCard({
  title,
  children,
  'data-testid': testId,
  noContentPadding,
  footer,
}: {
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  'data-testid'?: string;
  /**
   * When we have e.g. _just_ a table as the content, we skip the content padding
   *
   * If we have a table and other content, do not use this - wrap the table in `CardFullWidthContainer` instead
   */
  noContentPadding?: boolean;
}) {
  const { mode } = useMode();
  return (
    <CardRoot data-testid={testId} variant={mode === LEGACY_MODE ? 'outline' : 'elevated'}>
      {title ? (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      ) : null}
      {noContentPadding ? children : <CardBody>{children}</CardBody>}
      {footer ? <CardFooter>{footer}</CardFooter> : null}
    </CardRoot>
  );
}
